// Spec 65 i18n-framework — locale catalog.
//
// Two MVP locales per ADR-006: en-US (source language; technical
// reference + AI prompt assembly) and tr-TR (Turkish co-primary —
// TR-first market means we ship native Turkish UX, not translated
// English). Additional locales land in Phase 8+ following the
// Weblate translation pipeline.
//
// Each locale carries metadata the runtime needs:
//   * code           — BCP 47 tag, the Accept-Language / browser-canonical form
//   * name           — display label (in the locale's own script)
//   * englishName    — English label for log messages + admin UI
//   * direction      — "ltr" | "rtl"; affects layout mirror + caret direction
//   * dateFnsLocale  — date-fns locale module name (consumer dynamic-imports)
//   * decimalSeparator / thousandsSeparator — number-format defaults
//
// Spec 65 §entities.entity_locale would carry per-tenant overrides
// (force a specific decimal separator, override fallback chain) — that
// runtime CRUD lands in Phase 6+ admin console. The SDK ships the
// platform-default catalog only.

export type LocaleCode = "en-US" | "tr-TR";

export type LocaleDirection = "ltr" | "rtl";

export interface LocaleMetadata {
  readonly code: LocaleCode;
  readonly name: string;
  readonly englishName: string;
  readonly direction: LocaleDirection;
  readonly dateFnsLocale: string;
  readonly decimalSeparator: string;
  readonly thousandsSeparator: string;
}

export const SUPPORTED_LOCALES: readonly LocaleCode[] = ["en-US", "tr-TR"] as const;

export const DEFAULT_LOCALE: LocaleCode = "en-US";

export const LOCALE_METADATA: Readonly<Record<LocaleCode, LocaleMetadata>> = {
  "en-US": {
    code: "en-US",
    name: "English (US)",
    englishName: "English (US)",
    direction: "ltr",
    dateFnsLocale: "en-US",
    decimalSeparator: ".",
    thousandsSeparator: ",",
  },
  "tr-TR": {
    code: "tr-TR",
    name: "Türkçe",
    englishName: "Turkish",
    direction: "ltr",
    dateFnsLocale: "tr",
    decimalSeparator: ",",
    thousandsSeparator: ".",
  },
} as const;

export function isSupportedLocale(value: string): value is LocaleCode {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Resolve a locale preference per ADR-006:
 *   explicit user pref → tenant default → browser preferences → DEFAULT_LOCALE.
 *
 * Each candidate is tried in order: exact match (en-US) first, then
 * language-only (tr → tr-TR). Returns the FIRST candidate that maps to
 * a supported locale. Always returns SOMETHING — DEFAULT_LOCALE is the
 * floor.
 */
export function resolveLocale(opts: {
  userPref?: string;
  tenantDefault?: string;
  browserPreferences?: readonly string[];
}): LocaleCode {
  const candidates = [
    opts.userPref,
    opts.tenantDefault,
    ...(opts.browserPreferences ?? []),
  ].filter((c): c is string => typeof c === "string" && c.length > 0);

  for (const candidate of candidates) {
    // Exact match.
    if (isSupportedLocale(candidate)) return candidate;
    // Language-only match (tr → tr-TR / en → en-US).
    const languagePart = candidate.split("-")[0];
    if (languagePart === undefined) continue;
    const found = SUPPORTED_LOCALES.find((l) => l.startsWith(`${languagePart}-`));
    if (found !== undefined) return found;
  }
  return DEFAULT_LOCALE;
}
